"use client";

import { c as _c } from "react/compiler-runtime";
import { AboutContent } from "@shared-components/global/about-content";
import { Button } from "@shared-components/shadcn/button";
import { Card, CardFooter } from "@shared-components/shadcn/card";
import { LoaderEllipsis } from "@shared-components/loader-ellipsis";
import { SessionProvider, signIn, useSession } from "next-auth/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "next-auth";
import { useM365ApplicationState, useMS365Cloud } from "@ms-teams-poc/utils/hooks/m365cloud";
import { redirect, RedirectType, useSearchParams } from "next/navigation";
import { sanitizeReturnUrl } from "@shared-utils/sanitize-return-url";
import { SessionStatePersister } from "@shared-utils/hooks/session/session-state-persistener";
import { retry, ExponentialBackoff, handleAll } from "cockatiel";
interface AuthPromptProviderProps {
  children: ReactNode;
}
const AUTHENTICATED_STATUSES = new Set(["authenticated", "loading"]);
function createSessionRecoveryError(message: string): Error {
  const error = new Error(message);
  error.name = "SessionRecoveryError";
  return error;
}
const sessionRetryPolicy = retry(handleAll, {
  maxAttempts: 4,
  backoff: new ExponentialBackoff({
    initialDelay: 1000,
    maxDelay: 8000
  })
});
function LoginProvider(props: AuthPromptProviderProps) {
  const session = useSession();
  const ms365 = useMS365Cloud();
  const searchParams = useSearchParams();
  const {
    updateSourceContext
  } = useM365ApplicationState();

  // Get sanitized return URL for post-login redirect (preserves deeplinks)
  const returnUrl = sanitizeReturnUrl(searchParams.get("returnUrl"));

  // ==========================================================================
  // SESSION RECOVERY FOR TRANSIENT ERRORS (e.g., 504 Gateway Timeout)
  // ==========================================================================
  // Problem: When /api/auth/session returns a 504 or network error, next-auth
  // sets session.status to "unauthenticated" even though the user has a valid
  // session. This causes users to see the login screen unexpectedly.
  //
  // Solution: Cache the last valid session and retry fetching when we detect
  // a transition from authenticated to unauthenticated. During retries, we
  // continue showing the app content instead of the login screen.
  // ==========================================================================

  // Store the last successfully fetched session so we can detect transient failures
  const lastValidSession = useRef<Session | null>(null);

  // Track whether we're currently retrying to prevent multiple concurrent retries
  const [isRetrying, setIsRetrying] = useState(false);

  // Cache valid session whenever authentication succeeds
  useEffect(() => {
    if (session.status === "authenticated" && session.data) {
      lastValidSession.current = session.data;
    }
  }, [session.status, session.data]);

  // Store session.update in a ref to avoid unstable object reference in effect deps
  const sessionUpdateRef = useRef(session.update);
  useEffect(() => {
    sessionUpdateRef.current = session.update;
  }, [session.update]);

  // Retry session fetch when we detect a potential transient failure:
  // - Status changed to "unauthenticated"
  // - But we previously had a valid session (suggesting network error, not logout)
  useEffect(() => {
    // Track mount state to prevent setState after unmount
    let isMounted = true;
    const shouldRetry = session.status === "unauthenticated" && lastValidSession.current !== null && !isRetrying;
    if (shouldRetry) {
      setIsRetrying(true);
      void sessionRetryPolicy.execute(async () => {
        const result = await sessionUpdateRef.current();
        if (!result) {
          throw createSessionRecoveryError("Session fetch returned null");
        }
        return result;
      }).catch((error: unknown) => {
        if (error instanceof Error && error.name === "SessionRecoveryError") {
          console.warn("Session recovery failed after retries:", error.message);
          return;
        }
        throw error;
      }).finally(() => {
        if (isMounted) {
          setIsRetrying(false);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [session.status, isRetrying]);
  useEffect(() => {
    const isMSApplicationSource = session.data?.user.account?.applicationSource === "MS-SSO";
    const sourceContext = isMSApplicationSource ? "ms-sso" : "default";
    updateSourceContext(sourceContext);
  }, [session.data?.user.account?.applicationSource, updateSourceContext]);

  // ==========================================================================
  // RENDER DECISION LOGIC
  // ==========================================================================

  // Case 1: User is authenticated - show the app
  const hasValidSession = AUTHENTICATED_STATUSES.has(session.status) && session.data;

  // Case 2: Session fetch failed but we had a valid session and are retrying
  // Keep showing the app content while we attempt to recover the session
  const isRecoveringFromTransientError = lastValidSession.current && session.status === "unauthenticated" && isRetrying;
  if (hasValidSession || isRecoveringFromTransientError) {
    return props.children;
  }

  // Case 3: Initial session loading
  if (session.status === "loading") {
    return <LoaderEllipsis className="*:bg-ter-green-400" />;
  }

  // Case 4: MS Teams SSO users should be redirected to the Teams-specific flow
  if (session.status === "unauthenticated" && ms365.sourceContext === "ms-sso") {
    redirect("/ms-teams-poc", RedirectType.replace);
  }

  // Case 5: User is not authenticated and retries exhausted - show login
  return <div className="-mt-14 flex h-full w-full items-center justify-center">
      <Card className="min-w-[350px]">
        <AboutContent />
        <CardFooter>
          <Button className="w-full" onClick={() => void signIn("azure-ad", {
          redirect: true,
          callbackUrl: returnUrl
        })}>
            {"Sign in"}
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export function AuthPromptProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SessionStatePersister />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <SessionProvider refetchInterval={300} refetchOnWindowFocus={true}>{t1}<LoginProvider>{children}</LoginProvider></SessionProvider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}